import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { Seo, SITE_URL } from "@/components/Seo";
import { ARTICLES, BLOG_INTRO } from "@/data/articles";
import { ArrowRight, Clock } from "lucide-react";

const Label = ({ children, className = "" }) => (
    <span className={`font-mono text-[11px] uppercase tracking-[0.24em] ${className}`}>
        {children}
    </span>
);

const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

export default function Blog() {
    const blogJsonLd = {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "DentAssists Resources",
        description: BLOG_INTRO,
        url: `${SITE_URL}/blog`,
        blogPost: ARTICLES.map((a) => ({
            "@type": "BlogPosting",
            headline: a.title,
            description: a.description,
            datePublished: a.date,
            url: `${SITE_URL}/blog/${a.slug}`,
        })),
    };

    const [featured, ...rest] = ARTICLES;

    return (
        <div data-testid="blog-page" className="pt-[76px]">
            <Seo
                title="Dental Billing Resources for Texas Practices | DentAssists Blog"
                description="Guides on dental insurance verification, claim denials, accounts receivable, and credentialing for dental practices in Texas and across the U.S."
                path="/blog"
                jsonLd={blogJsonLd}
            />

            <section className="relative overflow-hidden">
                <div className="glow-terracotta absolute -top-24 -right-24 w-[520px] h-[520px] pointer-events-none" />
                <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10 pt-16 lg:pt-20 pb-12">
                    <Reveal>
                        <Label className="text-terracotta">Resources</Label>
                        <h1 className="font-heading text-5xl sm:text-6xl tracking-tight text-ink mt-6 max-w-3xl leading-[1]">
                            Dental billing, made understandable.
                        </h1>
                        <p className="text-base lg:text-lg text-inksoft leading-relaxed mt-7 max-w-2xl">
                            {BLOG_INTRO}
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Featured */}
            <section className="max-w-[1240px] mx-auto px-6 lg:px-10 pb-8">
                <Reveal>
                    <Link
                        to={`/blog/${featured.slug}`}
                        data-testid={`article-card-${featured.slug}`}
                        className="group grid lg:grid-cols-2 gap-8 lg:gap-12 items-center border border-linec rounded-2xl overflow-hidden hover:border-terracotta/50 transition-colors duration-300"
                    >
                        <div className="img-frame h-[260px] lg:h-[380px]">
                            <img src={featured.heroImage} alt={featured.title} className="w-full h-full object-cover" />
                        </div>
                        <div className="p-8 lg:pr-12">
                            <div className="flex items-center gap-3 text-inksoft">
                                <Label className="text-terracotta">{featured.category}</Label>
                                <span className="text-linec">·</span>
                                <span className="font-mono text-[11px] uppercase tracking-[0.16em]">
                                    {fmtDate(featured.date)}
                                </span>
                            </div>
                            <h2 className="font-heading text-3xl lg:text-4xl text-ink mt-4 leading-tight group-hover:text-sage transition-colors">
                                {featured.title}
                            </h2>
                            <p className="text-inksoft leading-relaxed mt-4">{featured.excerpt}</p>
                            <span className="inline-flex items-center gap-2 text-sage font-medium mt-6 group-hover:text-terracotta transition-colors">
                                Read article
                                <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
                            </span>
                        </div>
                    </Link>
                </Reveal>
            </section>

            {/* Grid */}
            <section className="max-w-[1240px] mx-auto px-6 lg:px-10 pb-24">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {rest.map((a, i) => (
                        <Reveal key={a.slug} delay={(i % 3) * 0.08}>
                            <Link
                                to={`/blog/${a.slug}`}
                                data-testid={`article-card-${a.slug}`}
                                className="group flex flex-col h-full border border-linec rounded-2xl overflow-hidden hover:border-terracotta/50 hover:-translate-y-1 transition-[transform,border-color] duration-300"
                            >
                                <div className="img-frame h-[190px]">
                                    <img src={a.heroImage} alt={a.title} className="w-full h-full object-cover" />
                                </div>
                                <div className="p-6 flex flex-col flex-1">
                                    <div className="flex items-center gap-2 text-inksoft">
                                        <Label className="text-terracotta">{a.category}</Label>
                                        <span className="text-linec">·</span>
                                        <span className="inline-flex items-center gap-1 text-xs text-inksoft">
                                            <Clock size={12} /> {a.readTime}
                                        </span>
                                    </div>
                                    <h3 className="font-heading text-xl text-ink mt-3 leading-snug group-hover:text-sage transition-colors">
                                        {a.title}
                                    </h3>
                                    <p className="text-sm text-inksoft leading-relaxed mt-3 flex-1">{a.excerpt}</p>
                                    <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-sage mt-5 inline-flex items-center gap-1">
                                        Read more <ArrowRight size={13} />
                                    </span>
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>
        </div>
    );
}
